import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { Icd10Diagnosis } from '@lib/dictionaries/core/models/icd10-diagnosis.interface';
import { DictionariesApiService } from '@lib/dictionaries/core/services/dictionaries-api.service';
import { CodingModel } from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, debounceTime, map, of, switchMap } from 'rxjs';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { OptionListComponent } from '../common/option-list.component';
import { OptionRowDirective } from '../common/option-row.directive';
import { SEARCH_DEBOUNCE_MS, SearchFieldComponent } from '../common/search-field.component';
import { DICTIONARY_PAGE_SIZE, byRelevance } from '../common/search-ranking';

/** The dictionary answers with two names; either may be the one a manager recognises. */
function describe(item: Icd10Diagnosis) {
  return { code: item.code, name: item.nameUa || item.nameEn };
}

/**
 * Picks an ICD-10 diagnosis from the dictionary rather than letting one be typed.
 *
 * Case search ranks on the exact code strings stored against a diagnosis, so a typo entered here
 * would create a code that matches nothing and quietly splits a member's case history.
 */
@Component({
  selector: 'guarantees-icd10-picker',
  standalone: true,
  imports: [MatIcon, MatTooltip, TranslatePipe, OptionListComponent, OptionRowDirective, SearchFieldComponent],
  templateUrl: './icd10-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Icd10PickerComponent {
  /** Pre-selects an existing coding when editing. */
  initial = input<CodingModel>();

  /**
   * Codes the list this picker feeds already holds. Offered but closed rather than filtered out: a
   * manager searching for a code they have already added must be told it is there, not left reading
   * an empty list as "the dictionary does not have it".
   */
  taken = input<readonly string[]>([]);

  selected = output<CodingModel>();

  private readonly dictionaries = inject(DictionariesApiService);

  protected readonly term = signal('');
  protected readonly results = signal<Icd10Diagnosis[]>([]);
  protected readonly searching = signal(false);
  protected readonly open = signal(false);

  /** What the field shows: the chosen diagnosis until the health manager types again. */
  protected readonly choice = signal<CodingModel | undefined>(undefined);

  protected readonly display = computed(() => {
    const chosen = this.choice() ?? this.initial();

    return chosen ? `${chosen.code} ${chosen.name ?? ''}`.trim() : '';
  });

  /** Read as a set rather than scanned per row: a dictionary page is long and the list is not. */
  private readonly takenCodes = computed(() => new Set(this.taken()));

  protected isTaken(code: string): boolean {
    return this.takenCodes().has(code);
  }

  constructor() {
    // `searching` is set at the keystroke and cleared only here, where the answer lands. Every
    // debounced term is re-asked rather than compared to the last: a term retyped within the
    // debounce would otherwise resolve nothing and leave the flag — and the spinner — stuck on.
    toObservable(this.term)
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        // A newer keystroke supersedes the request in flight, so a slow page cannot overwrite it.
        switchMap(value =>
          value.trim().length === 0
            ? of<Icd10Diagnosis[]>([])
            : this.dictionaries.searchIcd10Diagnoses({ searchTerm: value.trim(), pageSize: DICTIONARY_PAGE_SIZE, sortBy: 'code', sortDirection: 'Asc' }).pipe(
                map(response => [...(response.items ?? [])].sort(byRelevance(value.trim(), describe))),
                catchError(() => of<Icd10Diagnosis[]>([]))
              )
        ),
        takeUntilDestroyed()
      )
      .subscribe(items => {
        this.results.set(items);
        this.searching.set(false);
      });
  }

  protected search(value: string): void {
    this.term.set(value);
    // At the keystroke, not after the debounce: the quarter-second before the ask even starts is
    // already the search from the reader's side, and an empty list then must not claim "not found".
    this.searching.set(value.trim().length > 0);
    this.open.set(true);
  }

  protected choose(diagnosis: Icd10Diagnosis): void {
    if (this.isTaken(diagnosis.code)) {
      return;
    }

    const coding: CodingModel = { code: diagnosis.code, name: diagnosis.nameUa || diagnosis.nameEn };

    this.choice.set(coding);
    this.term.set('');
    this.results.set([]);
    this.open.set(false);
    this.selected.emit(coding);
  }
}
